import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Button, GrapheneRoot } from "@elsevier/graphene";
import ICA from "./Ica";
import FeedbackForm from "./FeedbackForm";
import { fetchJwtToken } from "./data-source/JwtToken";
import "./MockPage.css";
import FeedbackFormContext, {
  FeedbackFormConsumerHandler,
  FeedbackFormSaveResults,
} from "./context/FeedbackFormContext";

const MockPage = () => {
  const { extractionID = "" } = useParams<{ extractionID: string }>();
  const [jwtToken, setJwtToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!extractionID) return;

    let cancelled = false;

    async function loadJwt() {
      setIsLoading(true);
      setError(null);
      setJwtToken("");

      try {
        const result = await fetchJwtToken(extractionID);

        if (cancelled) return;

        if (result.error) {
          setError(result.error);
          return;
        }

        setJwtToken(result.token ?? "");
      } catch (e: unknown) {
        if (cancelled) return;

        if (e instanceof Error) {
          setError(e.message);
        } else {
          setError("Failed to fetch JWT token.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadJwt();

    return () => {
      cancelled = true;
    };
  }, [extractionID]);

  const handleSave = () => {};

  const saveRef = useRef<null | (() => Promise<FeedbackFormSaveResults>)>(null);
  const [saveStatus, setSaveStatus] = useState("");

  const onFeedbackFormInit = useCallback(
    (handlers: FeedbackFormConsumerHandler) => {
      saveRef.current = handlers.save;
    },
    [],
  );

  const providerValue = useMemo(
    () => ({ onFeedbackFormInit }),
    [onFeedbackFormInit],
  );

  const saveHandler = async () => {
    if (!saveRef.current) {
      setSaveStatus("Save handler not ready yet.");
      return;
    }

    setSaveStatus("Saving...");
    const res = await saveRef.current();

    if (res.status === "success") {
      setSaveStatus("Saved successfully");
    } else {
      setSaveStatus(`Save failed: ${res.errorMsg}`);
    }
  };

  return (
    <FeedbackFormContext.Provider value={providerValue}>
      <main id="main">
        <div className="main-container">
          <div className="wrapper">
            {isLoading && <p>Fetching JWT token...</p>}

            {error && !isLoading && <p className="error-message">{error}</p>}

            {!isLoading && !error && (
              <div className="mock-grid">
                <section className="mock-ica">
                  <GrapheneRoot palette="graphene-1-light">
                    <ICA extractionID={extractionID} jwtToken={jwtToken} />
                  </GrapheneRoot>
                </section>

                <aside className="mock-feedback">
                  <div className="mock-feedback-card">
                    <FeedbackForm
                      extractionID={extractionID}
                      jwtToken={jwtToken}
                      saveCallback={handleSave}
                    />
                  </div>
                  <div>
                    <Button
                      variant="primary"
                      size="medium"
                      label="Test Save"
                      onClick={saveHandler}
                    />
                    {saveStatus && (
                      <div style={{ marginTop: 8 }}>{saveStatus}</div>
                    )}
                  </div>
                </aside>
              </div>
            )}
          </div>
        </div>
      </main>
    </FeedbackFormContext.Provider>
  );
};

export default MockPage;
